import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'midnight' | 'ocean' | 'forest' | 'sunset';

const VALID_THEMES: Theme[] = ['light', 'dark', 'midnight', 'ocean', 'forest', 'sunset'];
const DARK_BASED: Theme[] = ['dark', 'midnight', 'ocean', 'forest'];

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: string) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function toSafeTheme(raw: string | null | undefined): Theme {
  if (raw && VALID_THEMES.includes(raw as Theme)) return raw as Theme;
  if (raw === 'system') return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() =>
    toSafeTheme(localStorage.getItem('everyday-os-theme'))
  );

  useEffect(() => {
    const root = document.documentElement;
    if (DARK_BASED.includes(theme)) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    root.setAttribute('data-theme', theme);
    localStorage.setItem('everyday-os-theme', theme);
  }, [theme]);

  const setTheme = (raw: string) => setThemeState(toSafeTheme(raw));

  const toggleTheme = () =>
    setThemeState(prev => (DARK_BASED.includes(prev) ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
